import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  EmptyState,
  Eyebrow,
  Field,
  FieldGroup,
  Fieldset,
  Input,
  Label,
  MediumText,
  NativeInput,
  NeutralPanel,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusText,
  StrongText,
  Subheading,
  SurfaceCard,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui'
import {
  ArrowUpTrayIcon,
  DocumentTextIcon,
  BeakerIcon,
  PhotoIcon,
  CubeIcon,
  FunnelIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid'
import { DEFAULT_UPLOAD_SOURCE, VAULT_CATEGORIES } from '../constants'
import { uiClasses } from '../../shared/uiClasses'
import type { ChangeEvent, DragEvent, FormEvent } from 'react'
import type {
  ActionResult,
  HealthRecord,
  RecordKind,
  UploadDraft,
} from '../types'

interface RecordsVaultTabProps {
  onAddRecord: (draft: UploadDraft) => Promise<ActionResult>
  onDeleteRecord: (recordId: string) => ActionResult
  records: HealthRecord[]
}

const KIND_COLORS: Record<RecordKind, 'amber' | 'cyan' | 'sky' | 'violet'> = {
  Imaging: 'sky',
  Lab: 'cyan',
  Medication: 'violet',
  'Visit Note': 'amber',
}
const KIND_ICONS: Record<RecordKind, typeof BeakerIcon> = {
  Lab: BeakerIcon,
  Imaging: PhotoIcon,
  'Visit Note': DocumentTextIcon,
  Medication: CubeIcon,
}

const TEXT_SECONDARY = uiClasses.textSecondary
const TEXT_SUBTLE = uiClasses.textSubtle
const TEXT_FAINT = uiClasses.textFaint
const DROPZONE_IDLE_BORDER = uiClasses.neutralBorder
const MB = 1024 * 1024
const KB = 1024
const RECORD_KIND_OPTIONS: RecordKind[] = ['Visit Note', 'Lab', 'Imaging', 'Medication']
const UPLOAD_SOURCE_AUTO = '__upload_source_auto__'
const UPLOAD_KIND_AUTO = '__upload_kind_auto__'
const PDF_ACCEPT = '.pdf,application/pdf'
const OPENROUTER_ERROR_PREFIX = '[OPENROUTER_'
const PDF_PREVIEW_MIN_ZOOM = 0.7
const PDF_PREVIEW_MAX_ZOOM = 3

type KindHintValue = '' | RecordKind

function normalizeKindHint(value: string): KindHintValue {
  for (const option of RECORD_KIND_OPTIONS) {
    if (option === value) {
      return option
    }
  }

  return ''
}

function formatBytes(bytes: number | null): string {
  if (bytes === null) {
    return '—'
  }

  if (bytes <= 0) {
    return '0 KB'
  }

  if (bytes >= MB) {
    return `${(bytes / MB).toFixed(1)} MB`
  }

  return `${Math.max(1, Math.round(bytes / KB))} KB`
}

function isPdfFile(file: File): boolean {
  if (file.type === 'application/pdf') {
    return true
  }

  return /\.pdf$/i.test(file.name)
}

function deriveTitleFromFileName(fileName: string): string {
  const withoutExtension = fileName.replace(/\.[^.]+$/, '')
  const normalized = withoutExtension.replace(/[_-]+/g, ' ').trim()
  return normalized || fileName
}

function createClientRecordId(): string {
  if (typeof globalThis.crypto?.randomUUID !== 'function') {
    throw new Error('[CRYPTO_UUID_UNAVAILABLE] Browser crypto.randomUUID() is required.')
  }

  return `record-${globalThis.crypto.randomUUID()}`
}

function createPdfPreviewUrl(file: File): string | null {
  if (typeof globalThis.URL?.createObjectURL !== 'function') {
    return null
  }

  try {
    return globalThis.URL.createObjectURL(file)
  } catch {
    return null
  }
}

function revokePdfPreviewUrl(previewUrl: string): void {
  if (typeof globalThis.URL?.revokeObjectURL !== 'function') {
    return
  }

  try {
    globalThis.URL.revokeObjectURL(previewUrl)
  } catch {
    // Ignore revoke errors for stale blob URLs.
  }
}

function resolveRecordSummary(record: HealthRecord): string {
  if (record.status === 'Failed') {
    return record.parseError?.trim() || 'Parsing failed.'
  }

  if (record.status === 'Parsing') {
    return 'Parsing document text...'
  }

  return record.parseSummary?.trim() || 'Parsed successfully. No report details were returned.'
}

function cleanBracketedErrorMessage(message: string): string {
  const cleaned = message.replace(/\[[A-Z0-9_]+\]\s*/g, '').trim()
  return cleaned || message
}

function isOpenRouterErrorMessage(rawMessage: string): boolean {
  return rawMessage.trim().startsWith(OPENROUTER_ERROR_PREFIX)
}

interface PdfPreviewViewport {
  height: number
  rawDims: {
    pageHeight: number
    pageWidth: number
    pageX: number
    pageY: number
  }
  rotation: number
  scale: number
  width: number
}

interface PdfPreviewRenderTask {
  promise: Promise<void>
}

interface PdfPreviewTextContent {
  items: unknown[]
}

interface PdfPreviewTextLayerRenderTask {
  cancel: () => void
  promise: Promise<void>
}

interface PdfPreviewPage {
  getTextContent: () => Promise<PdfPreviewTextContent>
  getViewport: (params: { scale: number }) => PdfPreviewViewport
  render: (params: { canvasContext: CanvasRenderingContext2D; viewport: PdfPreviewViewport }) => PdfPreviewRenderTask
}

interface PdfPreviewDocument {
  destroy?: () => void
  getPage: (pageNumber: number) => Promise<PdfPreviewPage>
  numPages: number
}

interface PdfPreviewLoadingTask {
  destroy?: () => void
  promise: Promise<PdfPreviewDocument>
}

interface PdfPreviewRuntime {
  getDocument: (source: { disableWorker?: boolean; url: string }) => PdfPreviewLoadingTask
  renderTextLayer: (params: {
    container: HTMLDivElement
    textContentSource: PdfPreviewTextContent
    viewport: PdfPreviewViewport
  }) => PdfPreviewTextLayerRenderTask
}

let pdfPreviewRuntimePromise: Promise<PdfPreviewRuntime> | null = null

async function loadPdfPreviewRuntime(): Promise<PdfPreviewRuntime> {
  if (!pdfPreviewRuntimePromise) {
    pdfPreviewRuntimePromise = (async () => {
      const [pdfjs] = await Promise.all([
        import('pdfjs-dist/legacy/build/pdf') as Promise<unknown>,
        import('pdfjs-dist/legacy/build/pdf.worker.entry'),
      ])
      if (
        typeof pdfjs !== 'object' ||
        pdfjs === null ||
        !('getDocument' in pdfjs) ||
        !('renderTextLayer' in pdfjs)
      ) {
        throw new Error('PDF preview runtime failed to initialize.')
      }

      return pdfjs as PdfPreviewRuntime
    })().catch((error) => {
      pdfPreviewRuntimePromise = null
      throw error
    })
  }

  return pdfPreviewRuntimePromise
}

interface CustomPdfPreviewProps {
  previewUrl: string | null
  recordTitle: string
}

interface PdfPreviewPageSize {
  height: number
  width: number
}

function hasSelectablePdfTextLayer(items: unknown[]): boolean {
  return items.some((item) => {
    if (typeof item !== 'object' || item === null || !('str' in item)) {
      return false
    }

    const text = (item as { str?: unknown }).str
    return typeof text === 'string' && text.trim().length > 0
  })
}

function cancelPdfTextLayerTask(task: PdfPreviewTextLayerRenderTask | null): void {
  if (!task) {
    return
  }

  try {
    task.cancel()
  } catch {
    // Ignore cancellation errors when rapidly navigating pages.
  }
}

export function CustomPdfPreview({ previewUrl, recordTitle }: CustomPdfPreviewProps) {
  const [pageCount, setPageCount] = useState(0)
  const [activePage, setActivePage] = useState(1)
  const [zoom, setZoom] = useState(1)
  const [loadState, setLoadState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle')
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [hasSelectableTextLayer, setHasSelectableTextLayer] = useState(false)
  const [isRenderingPage, setIsRenderingPage] = useState(false)
  const [renderedPageSize, setRenderedPageSize] = useState<PdfPreviewPageSize | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const textLayerRef = useRef<HTMLDivElement | null>(null)
  const pdfDocumentRef = useRef<PdfPreviewDocument | null>(null)
  const textLayerTaskRef = useRef<PdfPreviewTextLayerRenderTask | null>(null)

  useEffect(
    () => () => {
      pdfDocumentRef.current?.destroy?.()
      pdfDocumentRef.current = null
      cancelPdfTextLayerTask(textLayerTaskRef.current)
      textLayerTaskRef.current = null
    },
    []
  )

  useEffect(() => {
    pdfDocumentRef.current?.destroy?.()
    pdfDocumentRef.current = null
    setPageCount(0)
    setActivePage(1)
    setZoom(1)
    setPreviewError(null)
    setHasSelectableTextLayer(false)
    setIsRenderingPage(false)
    setRenderedPageSize(null)
    cancelPdfTextLayerTask(textLayerTaskRef.current)
    textLayerTaskRef.current = null
    if (textLayerRef.current) {
      textLayerRef.current.innerHTML = ''
    }

    if (!previewUrl) {
      setLoadState('idle')
      return
    }

    let isDisposed = false
    let loadingTask: PdfPreviewLoadingTask | null = null
    let loadedDocument: PdfPreviewDocument | null = null

    const loadDocument = async () => {
      setLoadState('loading')

      try {
        const runtime = await loadPdfPreviewRuntime()
        loadingTask = runtime.getDocument({
          disableWorker: true,
          url: previewUrl,
        })
        loadedDocument = await loadingTask.promise

        if (isDisposed) {
          loadedDocument.destroy?.()
          return
        }

        pdfDocumentRef.current = loadedDocument
        setPageCount(loadedDocument.numPages)
        setActivePage(1)
        setLoadState('ready')
      } catch (error) {
        if (isDisposed) {
          return
        }

        setPreviewError(
          error instanceof Error && error.message.trim()
            ? error.message.trim()
            : 'Unable to load PDF preview.'
        )
        setLoadState('error')
      }
    }

    void loadDocument()

    return () => {
      isDisposed = true
      loadingTask?.destroy?.()
      loadedDocument?.destroy?.()
      if (pdfDocumentRef.current === loadedDocument) {
        pdfDocumentRef.current = null
      }
    }
  }, [previewUrl])

  useEffect(() => {
    const canvas = canvasRef.current
    const pdfDocument = pdfDocumentRef.current
    if (!canvas || !pdfDocument || loadState !== 'ready') {
      return
    }

    const boundedPage = Math.min(Math.max(activePage, 1), pdfDocument.numPages)
    if (boundedPage !== activePage) {
      setActivePage(boundedPage)
      return
    }

    let isDisposed = false

    const renderPage = async () => {
      setIsRenderingPage(true)
      cancelPdfTextLayerTask(textLayerTaskRef.current)
      textLayerTaskRef.current = null
      const textLayerContainer = textLayerRef.current
      if (textLayerContainer) {
        textLayerContainer.innerHTML = ''
      }

      try {
        const page = await pdfDocument.getPage(boundedPage)
        const viewport = page.getViewport({ scale: zoom })
        const context = canvas.getContext('2d')
        if (!context) {
          throw new Error('Could not initialize PDF canvas.')
        }

        // Render to a higher-density backing canvas so text remains sharp on retina/high-DPI displays.
        const devicePixelRatio =
          typeof globalThis.window?.devicePixelRatio === 'number'
            ? Math.max(1, Math.min(globalThis.window.devicePixelRatio, 2.5))
            : 1
        const backingScale = Math.max(1.5, devicePixelRatio)

        canvas.width = Math.max(1, Math.floor(viewport.width * backingScale))
        canvas.height = Math.max(1, Math.floor(viewport.height * backingScale))
        canvas.style.width = `${viewport.width}px`
        canvas.style.height = `${viewport.height}px`
        context.setTransform(backingScale, 0, 0, backingScale, 0, 0)
        context.clearRect(0, 0, viewport.width, viewport.height)
        setRenderedPageSize({
          height: viewport.height,
          width: viewport.width,
        })

        const renderTask = page.render({
          canvasContext: context,
          viewport,
        })
        await renderTask.promise

        const textContent = await page.getTextContent()
        const nextHasSelectableTextLayer = hasSelectablePdfTextLayer(textContent.items)
        if (!isDisposed) {
          setHasSelectableTextLayer(nextHasSelectableTextLayer)
        }

        if (textLayerContainer && nextHasSelectableTextLayer) {
          textLayerContainer.style.setProperty('--scale-factor', `${viewport.scale}`)
          const runtime = await loadPdfPreviewRuntime()
          const textLayerTask = runtime.renderTextLayer({
            container: textLayerContainer,
            textContentSource: textContent,
            viewport,
          })
          textLayerTaskRef.current = textLayerTask
          await textLayerTask.promise
          if (textLayerTaskRef.current === textLayerTask) {
            textLayerTaskRef.current = null
          }
        }

        if (!isDisposed) {
          setPreviewError(null)
        }
      } catch (error) {
        if (isDisposed) {
          return
        }

        setPreviewError(
          error instanceof Error && error.message.trim()
            ? error.message.trim()
            : 'Unable to render PDF page.'
        )
        setHasSelectableTextLayer(false)
      } finally {
        if (!isDisposed) {
          setIsRenderingPage(false)
        }
      }
    }

    void renderPage()

    return () => {
      isDisposed = true
      cancelPdfTextLayerTask(textLayerTaskRef.current)
      textLayerTaskRef.current = null
    }
  }, [activePage, loadState, zoom])

  if (!previewUrl) {
    return (
      <div className="mt-2 flex h-[60vh] min-h-[18rem] items-center justify-center rounded-md bg-zinc-50 p-4">
        <EmptyState
          className="w-full !py-0"
          icon={DocumentTextIcon}
          title="No local PDF preview"
          description="Attach a PDF to this record to view it here."
          titleClassName={TEXT_SECONDARY}
          descriptionClassName={TEXT_FAINT}
          iconClassName={TEXT_FAINT}
        />
      </div>
    )
  }

  const canGoToPreviousPage = activePage > 1
  const canGoToNextPage = activePage < pageCount
  const canZoomOut = zoom > PDF_PREVIEW_MIN_ZOOM
  const canZoomIn = zoom < PDF_PREVIEW_MAX_ZOOM

  return (
    <>
      <div className="mt-2 flex items-center justify-between gap-3 rounded-md border border-zinc-200 bg-zinc-50 px-2 py-1.5">
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="unstyled"
            className="inline-flex h-7 w-7 items-center justify-center rounded border border-zinc-200 bg-white text-sm text-zinc-600 transition hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-40"
            onClick={() => setActivePage((current) => Math.max(1, current - 1))}
            disabled={!canGoToPreviousPage || loadState !== 'ready'}
            aria-label="Previous page"
          >
            &lsaquo;
          </Button>
          <Button
            type="button"
            variant="unstyled"
            className="inline-flex h-7 w-7 items-center justify-center rounded border border-zinc-200 bg-white text-sm text-zinc-600 transition hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-40"
            onClick={() => setActivePage((current) => Math.min(pageCount, current + 1))}
            disabled={!canGoToNextPage || loadState !== 'ready'}
            aria-label="Next page"
          >
            &rsaquo;
          </Button>
          <span className={`ml-1 text-xs/5 ${TEXT_SUBTLE}`}>
            Page {Math.min(activePage, Math.max(pageCount, 1))} of {Math.max(pageCount, 1)}
          </span>
          {loadState === 'ready' && !hasSelectableTextLayer && (
            <span className={`ml-2 hidden text-[11px]/5 sm:inline ${TEXT_FAINT}`}>
              No selectable text on this page
            </span>
          )}
        </div>

        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="unstyled"
            className="inline-flex h-7 w-7 items-center justify-center rounded border border-zinc-200 bg-white text-sm text-zinc-600 transition hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-40"
            onClick={() => {
              setZoom((current) =>
                Math.max(PDF_PREVIEW_MIN_ZOOM, Number((current - 0.1).toFixed(2)))
              )
            }}
            disabled={!canZoomOut || loadState !== 'ready'}
            aria-label="Zoom out"
          >
            -
          </Button>
          <span className={`w-11 text-center text-xs/5 ${TEXT_SUBTLE}`}>
            {Math.round(zoom * 100)}%
          </span>
          <Button
            type="button"
            variant="unstyled"
            className="inline-flex h-7 w-7 items-center justify-center rounded border border-zinc-200 bg-white text-sm text-zinc-600 transition hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-40"
            onClick={() => {
              setZoom((current) =>
                Math.min(PDF_PREVIEW_MAX_ZOOM, Number((current + 0.1).toFixed(2)))
              )
            }}
            disabled={!canZoomIn || loadState !== 'ready'}
            aria-label="Zoom in"
          >
            +
          </Button>
        </div>
      </div>

      <div className="relative mt-2 h-[60vh] min-h-[18rem] overflow-auto rounded-md bg-zinc-100">
        {(loadState === 'loading' || isRenderingPage) && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-zinc-100/75">
            <p className={`text-xs/5 ${TEXT_SUBTLE}`}>
              {loadState === 'loading' ? 'Loading PDF...' : 'Rendering page...'}
            </p>
          </div>
        )}

        {loadState === 'error' ? (
          <div className="flex h-full items-center justify-center p-4 text-center">
            <p className={`text-sm/6 ${TEXT_SUBTLE}`}>
              {previewError ?? 'Unable to load PDF preview.'}
            </p>
          </div>
        ) : (
          <div className="flex min-h-full items-start justify-center p-3">
            <div
              className="records-vault-pdf-page"
              style={
                renderedPageSize
                  ? {
                      height: `${renderedPageSize.height}px`,
                      width: `${renderedPageSize.width}px`,
                    }
                  : undefined
              }
            >
              <canvas
                ref={canvasRef}
                className="relative z-[1] rounded-sm bg-white shadow-[0_1px_2px_rgb(15_23_42/0.14)]"
                aria-label={`PDF preview canvas for ${recordTitle}`}
              />
              <div
                ref={textLayerRef}
                className="records-vault-pdf-text-layer"
                aria-hidden={!hasSelectableTextLayer}
              />
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default function RecordsVaultTab({
  onAddRecord,
  onDeleteRecord,
  records,
}: RecordsVaultTabProps) {
  const [vaultFilter, setVaultFilter] = useState<(typeof VAULT_CATEGORIES)[number]>('All')
  const [uploadTitleOverride, setUploadTitleOverride] = useState('')
  const [uploadSourceOverride, setUploadSourceOverride] = useState('')
  const [uploadKindOverride, setUploadKindOverride] = useState<KindHintValue>('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [recordActionMessage, setRecordActionMessage] = useState<string | null>(null)
  const [recordActionTone, setRecordActionTone] = useState<'danger' | 'success'>('success')
  const [isDraggingFile, setIsDraggingFile] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [detailRecord, setDetailRecord] = useState<HealthRecord | null>(null)
  const [recordPdfPreviewUrls, setRecordPdfPreviewUrls] = useState<Record<string, string>>({})
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const dragDepthRef = useRef(0)
  const recordActionTimerRef = useRef<number | null>(null)
  const recordPdfPreviewUrlsRef = useRef<Record<string, string>>({})

  function dismissRecordActionNotification() {
    if (recordActionTimerRef.current !== null) {
      window.clearTimeout(recordActionTimerRef.current)
      recordActionTimerRef.current = null
    }

    setRecordActionMessage(null)
  }

  function showRecordActionNotification(
    tone: 'danger' | 'success',
    message: string
  ) {
    if (recordActionTimerRef.current !== null) {
      window.clearTimeout(recordActionTimerRef.current)
    }

    setRecordActionTone(tone)
    setRecordActionMessage(message)
    recordActionTimerRef.current = window.setTimeout(() => {
      setRecordActionMessage(null)
      recordActionTimerRef.current = null
    }, 3200)
  }

  useEffect(
    () => () => {
      if (recordActionTimerRef.current !== null) {
        window.clearTimeout(recordActionTimerRef.current)
      }
    },
    []
  )

  useEffect(() => {
    recordPdfPreviewUrlsRef.current = recordPdfPreviewUrls
  }, [recordPdfPreviewUrls])

  useEffect(
    () => () => {
      for (const previewUrl of Object.values(recordPdfPreviewUrlsRef.current)) {
        revokePdfPreviewUrl(previewUrl)
      }
    },
    []
  )

  useEffect(() => {
    const activeRecordIds = new Set(records.map((record) => record.id))
    setRecordPdfPreviewUrls((current) => {
      let changed = false
      const next: Record<string, string> = {}

      for (const [recordId, previewUrl] of Object.entries(current)) {
        if (activeRecordIds.has(recordId)) {
          next[recordId] = previewUrl
          continue
        }

        revokePdfPreviewUrl(previewUrl)
        changed = true
      }

      return changed ? next : current
    })
  }, [records])

  const filteredRecords = useMemo(
    () => (vaultFilter === 'All' ? records : records.filter((record) => record.kind === vaultFilter)),
    [records, vaultFilter]
  )

  const failedRecordsCount = records.filter((record) => record.status === 'Failed').length
  const detailRecordPdfPreviewUrl = detailRecord ? recordPdfPreviewUrls[detailRecord.id] ?? null : null

  function attachSessionPdfPreview(recordId: string, previewUrl: string | null) {
    if (!previewUrl) {
      return
    }

    setRecordPdfPreviewUrls((current) => {
      const previousPreviewUrl = current[recordId]
      if (previousPreviewUrl && previousPreviewUrl !== previewUrl) {
        revokePdfPreviewUrl(previousPreviewUrl)
      }

      return {
        ...current,
        [recordId]: previewUrl,
      }
    })
  }

  function shouldRetainSessionPreviewOnFailure(rawMessage: string): boolean {
    const normalized = rawMessage.trim().toUpperCase()
    if (normalized.startsWith('[UPLOAD_FILE_REQUIRED]')) {
      return false
    }
    if (normalized.startsWith('[UPLOAD_METADATA_INVALID]')) {
      return false
    }
    if (normalized.startsWith('[OPENROUTER_NOT_CONFIGURED]')) {
      return false
    }

    // Parse failures (including OpenRouter HTTP/model errors) should retain preview.
    return true
  }

  function clearSelectedFile(options?: { preserveError?: boolean }) {
    dragDepthRef.current = 0
    setIsDraggingFile(false)
    setSelectedFile(null)
    setUploadTitleOverride('')
    setUploadSourceOverride('')
    setUploadKindOverride('')
    if (!options?.preserveError) {
      setUploadError(null)
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  function applySelectedFile(file: File | null) {
    if (isUploading || !file) {
      return
    }

    if (!isPdfFile(file)) {
      clearSelectedFile({ preserveError: true })
      setUploadError('Only PDF files are supported right now.')
      return
    }

    setUploadError(null)
    setSelectedFile(file)
    setUploadTitleOverride('')
    setUploadSourceOverride('')
    setUploadKindOverride('')
  }

  function handleFileSelection(event: ChangeEvent<HTMLInputElement>) {
    const nextFile = event.target.files?.[0] ?? null
    applySelectedFile(nextFile)
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault()
    if (isUploading) {
      return
    }

    dragDepthRef.current = 0
    setIsDraggingFile(false)
    const nextFile = event.dataTransfer.files?.[0] ?? null
    applySelectedFile(nextFile)
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (!selectedFile) {
      setUploadError('Please select a PDF file to upload.')
      return
    }

    let draftRecordId = ''
    try {
      draftRecordId = createClientRecordId()
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Could not prepare record upload.')
      return
    }

    const draftPdfPreviewUrl = createPdfPreviewUrl(selectedFile)
    setUploadError(null)
    dismissRecordActionNotification()
    setIsUploading(true)

    let result: ActionResult
    try {
      result = await onAddRecord({
        id: draftRecordId,
        file: selectedFile,
        fileSizeBytes: selectedFile.size,
        kind: uploadKindOverride || undefined,
        source: uploadSourceOverride.trim() || undefined,
        title: uploadTitleOverride.trim() || undefined,
      })
    } catch (error) {
      const failureMessage =
        error instanceof Error && error.message.trim()
          ? error.message.trim()
          : '[UPLOAD_PARSE_FAILED] Upload parsing failed.'
      const shouldRetainPreview = shouldRetainSessionPreviewOnFailure(failureMessage)
      if (shouldRetainPreview) {
        attachSessionPdfPreview(draftRecordId, draftPdfPreviewUrl)
      } else if (draftPdfPreviewUrl) {
        revokePdfPreviewUrl(draftPdfPreviewUrl)
      }

      if (handleOpenRouterUploadError(failureMessage)) {
        setUploadError(null)
        setIsUploading(false)
        return
      }

      setUploadError(failureMessage)
      setIsUploading(false)
      return
    }

    if (!result.ok) {
      const failureMessage =
        'error' in result
          ? result.error
          : '[UPLOAD_PARSE_FAILED] Upload parsing failed.'
      const shouldRetainPreview = shouldRetainSessionPreviewOnFailure(failureMessage)
      if (shouldRetainPreview) {
        attachSessionPdfPreview(draftRecordId, draftPdfPreviewUrl)
      } else if (draftPdfPreviewUrl) {
        revokePdfPreviewUrl(draftPdfPreviewUrl)
      }

      if (handleOpenRouterUploadError(failureMessage)) {
        setUploadError(null)
        setIsUploading(false)
        return
      }

      setUploadError(failureMessage)
      setIsUploading(false)
      return
    }

    attachSessionPdfPreview(draftRecordId, draftPdfPreviewUrl)
    clearSelectedFile()
    setIsUploading(false)
  }

  const derivedTitle = selectedFile ? deriveTitleFromFileName(selectedFile.name) : ''

  function handleDeleteRecordClick(record: HealthRecord) {
    const shouldDelete = window.confirm(
      `Remove "${record.title}" from local records? This action cannot be undone.`
    )
    if (!shouldDelete) {
      return
    }

    const result = onDeleteRecord(record.id)
    if (!result.ok) {
      showRecordActionNotification(
        'danger',
        'error' in result
          ? result.error
          : '[RECORD_DELETE_FAILED] Failed to remove record.'
      )
      return
    }

    showRecordActionNotification('success', `Record "${record.title}" removed.`)
    setRecordPdfPreviewUrls((current) => {
      const previewUrl = current[record.id]
      if (!previewUrl) {
        return current
      }

      revokePdfPreviewUrl(previewUrl)
      const next = { ...current }
      delete next[record.id]
      return next
    })
    setDetailRecord((current) => (current?.id === record.id ? null : current))
  }

  function handleOpenRouterUploadError(rawMessage: string): boolean {
    if (!isOpenRouterErrorMessage(rawMessage)) {
      return false
    }

    showRecordActionNotification('danger', cleanBracketedErrorMessage(rawMessage))
    return true
  }

  return (
    <div className="space-y-6">
      {recordActionMessage && (
        <div className="records-vault-toast-wrapper" aria-live="polite">
          <div
            className={`records-vault-toast ${
              recordActionTone === 'danger'
                ? 'records-vault-toast-danger'
                : 'records-vault-toast-success'
            }`}
            role="status"
          >
            <p className="records-vault-toast-message">{recordActionMessage}</p>
            <Button
              type="button"
              variant="unstyled"
              className="records-vault-toast-close"
              aria-label="Dismiss notification"
              onClick={dismissRecordActionNotification}
            >
              <XMarkIcon className="size-4" />
            </Button>
          </div>
        </div>
      )}
      <SurfaceCard as="article">
        <div
          className={`records-vault-choose-pdf-dropzone mt-6 flex min-h-[13.5rem] items-center justify-center rounded-xl border-2 border-dotted p-8 text-center ${
            isDraggingFile ? 'border-accent bg-accent/5' : DROPZONE_IDLE_BORDER
          } ${
            isUploading ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'
          }`}
          role="button"
          tabIndex={isUploading ? -1 : 0}
          aria-disabled={isUploading}
          onClick={() => {
            if (isUploading) {
              return
            }
            fileInputRef.current?.click()
          }}
          onKeyDown={(event) => {
            if (isUploading) {
              return
            }

            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              fileInputRef.current?.click()
            }
          }}
          onDragEnter={() => {
            if (isUploading) {
              return
            }

            dragDepthRef.current += 1
            setIsDraggingFile(true)
          }}
          onDragLeave={() => {
            if (isUploading) {
              return
            }

            dragDepthRef.current = Math.max(0, dragDepthRef.current - 1)
            if (dragDepthRef.current === 0) {
              setIsDraggingFile(false)
            }
          }}
          onDragOver={(event) => {
            event.preventDefault()
            if (isUploading) {
              return
            }

            if (!isDraggingFile) {
              setIsDraggingFile(true)
            }
          }}
          onDrop={handleDrop}
        >
          <div className="max-w-sm">
            <span className="mx-auto inline-flex size-12 items-center justify-center rounded-full bg-zinc-100">
              <ArrowUpTrayIcon className={`size-6 ${TEXT_FAINT}`} />
            </span>
            <MediumText className="mt-3" tone="secondary">
              {isDraggingFile ? 'Drop PDF to upload' : 'Drag and drop your PDF here'}
            </MediumText>
            <p className={`mt-1 text-xs/5 ${TEXT_FAINT}`}>or choose a file from your device</p>
            <div className="mt-4">
              <Button
                type="button"
                className=""
                onClick={(event) => {
                  event.stopPropagation()
                  fileInputRef.current?.click()
                }}
                disabled={isUploading}
              >
                Browse PDF
              </Button>
            </div>
            <label htmlFor="upload-pdf-file" className="sr-only">PDF file</label>
            <NativeInput
              id="upload-pdf-file"
              ref={fileInputRef}
              type="file"
              accept={PDF_ACCEPT}
              className="sr-only"
              onChange={handleFileSelection}
              disabled={isUploading}
            />
          </div>
        </div>

        {selectedFile && (
          <NeutralPanel className="mt-3 flex items-center justify-between gap-3 px-3 py-2">
            <p className={`min-w-0 text-sm/6 ${TEXT_SECONDARY}`}>
              Selected file: <span className="font-medium">{selectedFile.name}</span> ({formatBytes(selectedFile.size)})
            </p>
            <Button
              type="button"
              variant="outline"
              className="shrink-0"
              onClick={() => clearSelectedFile()}
              disabled={isUploading}
            >
              <XMarkIcon data-slot="icon" />
              Remove
            </Button>
          </NeutralPanel>
        )}

        {uploadError && (
          <NeutralPanel tone="info" className="mt-3 px-3 py-2">
            <StatusText aria-live="polite">{uploadError}</StatusText>
          </NeutralPanel>
        )}

        <form onSubmit={(event) => void handleSubmit(event)} className="mt-4">
          <Fieldset>
            <FieldGroup>
              {selectedFile && (
                <NeutralPanel tone="muted" className="rounded-lg p-4">
                  <details>
                    <MediumText as="summary" className="cursor-pointer select-none" tone="secondary">
                      Edit details
                    </MediumText>
                    <p className={`mt-1 text-xs/5 ${TEXT_FAINT}`}>
                      Override auto-detected values. Leave blank to use defaults.
                    </p>
                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                      <Field className="sm:col-span-2">
                        <Label htmlFor="upload-title-override">Title</Label>
                        <Input
                          id="upload-title-override"
                          value={uploadTitleOverride}
                          onChange={(event) => setUploadTitleOverride(event.target.value)}
                          placeholder={derivedTitle || 'Auto from file name'}
                        />
                      </Field>
                      <Field>
                        <Label htmlFor="upload-source-override">Source</Label>
                        <Select
                          value={uploadSourceOverride || undefined}
                          onValueChange={(nextValue) =>
                            setUploadSourceOverride(nextValue === UPLOAD_SOURCE_AUTO ? '' : nextValue)
                          }
                        >
                          <SelectTrigger id="upload-source-override">
                            <SelectValue placeholder={`Auto (${DEFAULT_UPLOAD_SOURCE})`} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={UPLOAD_SOURCE_AUTO}>Auto ({DEFAULT_UPLOAD_SOURCE})</SelectItem>
                            <SelectItem value="Provider Portal">Provider Portal</SelectItem>
                            <SelectItem value="Quest Diagnostics">Quest Diagnostics</SelectItem>
                            <SelectItem value="Imaging Center Export">Imaging Center Export</SelectItem>
                          </SelectContent>
                        </Select>
                      </Field>
                      <Field>
                        <Label htmlFor="upload-kind-override">Record type</Label>
                        <Select
                          value={uploadKindOverride || undefined}
                          onValueChange={(nextValue) =>
                            setUploadKindOverride(
                              normalizeKindHint(nextValue === UPLOAD_KIND_AUTO ? '' : nextValue)
                            )
                          }
                        >
                          <SelectTrigger id="upload-kind-override">
                            <SelectValue placeholder="Auto detect" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={UPLOAD_KIND_AUTO}>Auto detect</SelectItem>
                            {RECORD_KIND_OPTIONS.map((kind) => (
                              <SelectItem key={kind} value={kind}>
                                {kind}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                    </div>
                  </details>
                </NeutralPanel>
              )}

              {selectedFile && (
                <Button
                  type="submit"
                  className="records-vault-upload-btn"
                  disabled={isUploading}
                >
                  <PlusIcon data-slot="icon" />
                  {isUploading ? 'Uploading...' : 'Upload and parse'}
                </Button>
              )}
            </FieldGroup>
          </Fieldset>
        </form>
      </SurfaceCard>

      <SurfaceCard as="article">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Subheading variant="dashboard">
            {vaultFilter === 'All' ? 'All records' : vaultFilter}
            <span className={`ml-2 text-sm font-normal ${TEXT_FAINT}`}>({filteredRecords.length})</span>
          </Subheading>
          <div className="flex flex-wrap gap-2">
            {VAULT_CATEGORIES.map((category) => (
              <Button
                key={category}
                type="button"
                variant="chip"
                size="chipWide"
                data-active={vaultFilter === category ? 'true' : undefined}
                onClick={() => setVaultFilter(category)}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
        <Table
          className="records-vault-table mt-4"
          surface
          gutter="none"
          dense
        >
          <TableHeader className="records-vault-table-head">
            <TableRow>
              <TableHead className="records-vault-table-col">Record</TableHead>
              <TableHead className="records-vault-table-col">Source</TableHead>
              <TableHead className="records-vault-table-col">Type</TableHead>
              <TableHead className="records-vault-table-col text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRecords.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <EmptyState
                    icon={records.length === 0 ? DocumentTextIcon : FunnelIcon}
                    title={records.length === 0 ? 'No records yet' : 'No records match this filter'}
                    description={
                      records.length === 0
                        ? 'Upload your first PDF above.'
                        : 'Try clearing filters to view all records.'
                    }
                    titleClassName={TEXT_SECONDARY}
                    descriptionClassName={TEXT_SUBTLE}
                    iconClassName={TEXT_FAINT}
                  >
                    {records.length > 0 && vaultFilter !== 'All' && (
                      <Button
                        type="button"
                        variant="unstyled"
                        className="text-sm font-medium text-accent underline"
                        onClick={() => setVaultFilter('All')}
                      >
                        Clear filter
                      </Button>
                    )}
                  </EmptyState>
                </TableCell>
              </TableRow>
            ) : (
              filteredRecords.map((record) => {
                const Icon = KIND_ICONS[record.kind]

                return (
                  <TableRow
                    key={record.id}
                    className="records-vault-row cursor-pointer"
                    tabIndex={0}
                    onClick={() => setDetailRecord(record)}
                    onKeyDown={(event) => {
                      if (event.target !== event.currentTarget) {
                        return
                      }
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        setDetailRecord(record)
                      }
                    }}
                  >
                    <TableCell>
                      <div className="records-vault-record-main">
                        <span className="records-vault-record-icon" aria-hidden="true">
                          <Icon className={`size-4 ${TEXT_SUBTLE}`} />
                        </span>
                        <div className="min-w-0">
                          <StrongText className="max-w-[22rem] truncate" title={record.title}>
                            {record.title}
                          </StrongText>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className={`max-w-[13rem] truncate text-sm/6 ${TEXT_SECONDARY}`} title={record.source}>
                        {record.source}
                      </p>
                    </TableCell>
                    <TableCell>
                      <Badge variant={KIND_COLORS[record.kind]}>{record.kind}</Badge>
                    </TableCell>
                    <TableCell className="records-vault-action-cell text-right align-middle">
                      <Button
                        type="button"
                        variant="outline"
                        className="records-vault-delete-btn"
                        aria-label={`Delete ${record.title}`}
                        onClick={(event) => {
                          event.stopPropagation()
                          handleDeleteRecordClick(record)
                        }}
                      >
                        <TrashIcon data-slot="icon" />
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
        {failedRecordsCount > 0 && (
          <StatusText className="mt-3" size="xs" tone="danger" aria-live="polite">
            {failedRecordsCount} record{failedRecordsCount === 1 ? '' : 's'} failed to parse. Re-upload the original PDF to retry with updated parser settings.
          </StatusText>
        )}
        {detailRecord && (
          <Dialog open onOpenChange={(open) => !open && setDetailRecord(null)}>
              <DialogContent size="xl">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <DialogTitle>
                      {detailRecord.title}
                    </DialogTitle>
                    <p className={`mt-1 text-sm/6 ${TEXT_SUBTLE}`}>
                      {detailRecord.kind} • {detailRecord.source}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className=""
                    onClick={() => setDetailRecord(null)}
                  >
                    Close
                  </Button>
                </div>

                <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
                  <div className="space-y-4">
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
                      <NeutralPanel tone="muted" className="rounded-lg p-3">
                        <Eyebrow>Status</Eyebrow>
                        <p className={`mt-1 text-sm/6 ${TEXT_SECONDARY}`}>{detailRecord.status}</p>
                      </NeutralPanel>
                      <NeutralPanel tone="muted" className="rounded-lg p-3">
                        <Eyebrow>Updated</Eyebrow>
                        <p className={`mt-1 text-sm/6 ${TEXT_SECONDARY}`}>{detailRecord.updatedAt}</p>
                      </NeutralPanel>
                      <NeutralPanel tone="muted" className="rounded-lg p-3">
                        <Eyebrow>File size</Eyebrow>
                        <p className={`mt-1 text-sm/6 ${TEXT_SECONDARY}`}>{formatBytes(detailRecord.fileSizeBytes)}</p>
                      </NeutralPanel>
                      <NeutralPanel tone="muted" className="rounded-lg p-3">
                        <Eyebrow>Record type</Eyebrow>
                        <p className={`mt-1 text-sm/6 ${TEXT_SECONDARY}`}>{detailRecord.kind}</p>
                      </NeutralPanel>
                    </div>

                    <NeutralPanel tone="muted" className="rounded-lg p-3">
                      <Eyebrow>
                        {detailRecord.status === 'Parsed' ? 'Report details' : 'Details'}
                      </Eyebrow>
                      <p className={`mt-2 text-sm/6 ${TEXT_SECONDARY}`}>{resolveRecordSummary(detailRecord)}</p>
                    </NeutralPanel>
                  </div>

                  <NeutralPanel tone="solid" className="rounded-lg border-zinc-200/80 p-2 sm:p-3">
                    <div className="flex items-start justify-between gap-3 px-1">
                      <div>
                        <p className={`text-xs/5 ${TEXT_FAINT}`}>PDF preview</p>
                        <p className={`text-[11px]/5 ${TEXT_FAINT}`}>
                          PDF preview is available for records uploaded in this session.
                        </p>
                      </div>
                      {detailRecordPdfPreviewUrl && (
                        <MediumText
                          as="a"
                          href={detailRecordPdfPreviewUrl}
                          target="_blank"
                          rel="noreferrer"
                          size="xs"
                          tone="subtle"
                          className="hover:text-zinc-900"
                        >
                          Open in new tab
                        </MediumText>
                      )}
                    </div>
                    <CustomPdfPreview
                      previewUrl={detailRecordPdfPreviewUrl}
                      recordTitle={detailRecord.title}
                    />
                  </NeutralPanel>
                </div>
              </DialogContent>
          </Dialog>
        )}
      </SurfaceCard>
    </div>
  )
}
